import { useMemo } from "react"
import type { NavigationOrgProps } from "@onestore/hel/dist/components/organisms/PageHeaderOrg/components/MainOrg/NavigationOrg"
import type { NavigationDropdownContentOrgProps } from "@onestore/hel/dist/components/organisms/PageHeaderOrg/components/MainOrg/NavigationOrg/NavigationDropdownOrg/NavigationDropdownContentOrg"
import type { NavigationCtaAtmProps } from "@onestore/hel/dist/components/organisms/PageHeaderOrg/components/MainOrg/NavigationOrg/NavigationListOrg/NavigationCtaAtm"
import type { LinkElement } from "@gatsby-plugin-generic-page/fragments/linkElement"
import getLinkProps from "@gatsby-plugin-generic-page/helpers/getLinkProps"
import { SquidexComponentType } from "@gatsby-plugin-generic-page/types/componentType"
import type { Dropdown } from "@gatsby-plugin-onestore/fragments/dropdown"
import type { DropdownLink } from "@gatsby-plugin-onestore/fragments/dropdownLink"
import type { Navigation } from "@gatsby-plugin-onestore/fragments/navigation"
import buildNavIconElement from "@gatsby-plugin-onestore/helpers/buildNavIconElement"
import { typeMatches } from "@onestore-graphql"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import isEmpty from "../../../../src/lib/isEmpty"

type LinkProps = NavigationDropdownContentOrgProps["links"]

type AditionalLink = NavigationDropdownContentOrgProps["additionalLink"]

function useNavigation(nav: Navigation[]): NavigationOrgProps["navigation"] {
  const { currentPriceType } = usePriceTypeContext()

  const navigation: NavigationOrgProps["navigation"] = useMemo(
    () =>
      nav.map((element) => {
        let cta: NavigationCtaAtmProps,
          dropdown: NavigationDropdownContentOrgProps | undefined

        const __typename = element.navElements[0].__typename

        const iconElement = buildNavIconElement(element.icon)

        if (typeMatches(__typename, SquidexComponentType.LINK)) {
          const { text, title, href, to, onClick } = getLinkProps(
            element.navElements[0] as LinkElement,
            currentPriceType
          )

          cta = {
            text,
            href,
            to,
            title,
            onClick,
            icon: iconElement,
          }
        } else {
          cta = {
            text: element.name,
            icon: iconElement,
          }
        }

        if (typeMatches(__typename, SquidexComponentType.DROPDOWN)) {
          const { links, additionalLink, asideLinks, title } = element
            .navElements[0] as Dropdown

          const dropDownLinks: LinkProps = links.map(
            (dropdownLink: DropdownLink) => {
              const { href, to, onClick, text, title, openInNewTab } =
                getLinkProps(dropdownLink.link, currentPriceType)

              return {
                title: text,
                subtitle: dropdownLink.subtitle,
                href,
                to,
                linkTitle: title,
                label: dropdownLink.label,
                onClick,
                openInNewTab,
              }
            }
          )

          const dropdownAsideLinks: LinkProps | undefined = !isEmpty(asideLinks)
            ? asideLinks.map((link: LinkElement) => {
                const { href, to, onClick, text, title, openInNewTab } =
                  getLinkProps(link, currentPriceType)

                return {
                  title: text,
                  href,
                  to,
                  linkTitle: title,
                  onClick,
                  openInNewTab,
                }
              })
            : undefined

          const additionalLinkData = !isEmpty(additionalLink)
            ? getLinkProps(additionalLink, currentPriceType)
            : null

          const dropdownAdditionalLink: AditionalLink = !isEmpty(
            additionalLinkData
          )
            ? {
                text: additionalLinkData.text,
                href: additionalLinkData.href,
                to: additionalLinkData.to,
                title: additionalLinkData.title,
                onClick: additionalLinkData.onClick,
              }
            : undefined

          dropdown = {
            title,
            links: dropDownLinks,
            additionalLink: dropdownAdditionalLink,
            asideLinks: dropdownAsideLinks,
          }
        } else dropdown = undefined

        return {
          cta,
          dropdown,
        }
      }),
    []
  )

  return navigation
}

export default useNavigation
